import { ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/atoms/button';
import { DataTable, type DataTableColumnDef } from '@/components/molecules/DataTable';
import { ErrorState } from '@/components/molecules/ErrorState';
import { Loading } from '@/components/molecules/Loading';
import { ZONE_PARAM } from '@/features/map/hooks/useMapSelection';
import { COUNTRY_LABELS } from '@/features/zones/constants/markets';
import { formatOwnerName } from '@/libs/utils/formatOwnerName';
import { isSafeHttpUrl } from '@/features/zones/utils/isSafeHttpUrl';
import {
  ZONES_MIXPANEL_EVENTS,
  type ZoneRowOpenedPayload,
} from '@/features/zones/utils/mixpanelEvents';
import { useMixpanel } from '@/hooks/useMixpanel';
import { cn } from '@/libs/utils/cn';
import type { IZoneSummary } from '@/types/api';

function formatSyncedAt(iso: string): string {
  const d = new Date(iso);
  // `new Date(badInput)` returns an Invalid Date rather than throwing, so a
  // try/catch is useless here — check the timestamp instead.
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

const columns: DataTableColumnDef<IZoneSummary>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  {
    accessorKey: 'country',
    header: 'Country',
    cell: ({ row }) => (row.original.country ? COUNTRY_LABELS[row.original.country] : '—'),
  },
  {
    accessorKey: 'owner_id',
    header: 'owner',
    cell: ({ row }) =>
      row.original.owner_id ? (
        <span title={row.original.owner_id}>{formatOwnerName(row.original.owner_id)}</span>
      ) : (
        <span className="text-muted-foreground">Unassigned</span>
      ),
  },
  {
    accessorKey: 'last_synced_at',
    header: 'Last synced',
    cell: ({ row }) => {
      const lastSyncedAt = row.original.last_synced_at;
      if (!lastSyncedAt) return '—';
      return (
        <time className="text-muted-foreground" dateTime={lastSyncedAt}>
          {formatSyncedAt(lastSyncedAt)}
        </time>
      );
    },
  },
  {
    id: 'source',
    header: 'Source',
    cell: ({ row }) => {
      const url = row.original.notion_page_url;
      if (!url || !isSafeHttpUrl(url)) return '—';
      return (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          // The row navigates to the map; this cell keeps opening Notion. Both
          // handlers are guarded — DataTable activates a row on click AND on
          // Enter/Space, so a keyboard user on the link needs the same escape.
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
          }}
          className="flex items-center gap-1 text-primary-600 hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          Notion
        </a>
      );
    },
  },
];

interface ZonesDashboardReadOnlyProps {
  zones: IZoneSummary[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  isPlaceholderData?: boolean;
}

/**
 * Read-only Zone Management dashboard. Renders the
 * Notion-sourced zones as a table — name, country, owner, last-synced
 * time, and a Notion deep link per row. There is no edit pencil, no
 * country/BD dropdown, no bulk-select, and no Pending → Active control:
 * Notion is the single editable surface, and the sync workflow is the sole
 * writer. The only interaction is navigation: a row opens the zone on the
 * map, asking the map to focus it via router state (`?zone=` alone is
 * camera-neutral — see MapZoneFocus).
 */
export function ZonesDashboardReadOnly({
  zones,
  isLoading,
  isError,
  onRetry,
  isPlaceholderData,
}: ZonesDashboardReadOnlyProps) {
  const navigate = useNavigate();
  const trackAction = useMixpanel((state) => state.trackAction);

  if (isLoading) {
    return <Loading content="Loading zones…" />;
  }

  if (isError) {
    return (
      <ErrorState
        title="Could not load zones"
        description="Refresh the page or try again."
        actions={
          <Button variant="outline" onClick={onRetry}>
            Retry
          </Button>
        }
      />
    );
  }

  if (zones.length === 0) {
    return (
      <p className="mt-8 text-center text-sm text-muted-foreground">
        No zones yet. Sync from Notion to populate the dashboard.
      </p>
    );
  }

  return (
    <div
      aria-busy={isPlaceholderData ?? false}
      className={cn(
        'transition-opacity duration-200',
        isPlaceholderData && 'pointer-events-none opacity-60',
      )}
    >
      {isPlaceholderData && (
        <span role="status" aria-live="polite" className="sr-only">
          Loading next page…
        </span>
      )}
      <DataTable
        columns={columns}
        data={zones}
        onRowClick={(zone) => {
          const payload: ZoneRowOpenedPayload = {
            zone_id: zone.id,
            country: zone.country ?? null,
            has_owner: zone.owner_id != null,
          };
          trackAction(ZONES_MIXPANEL_EVENTS.ROW_OPENED, payload);
          navigate(`/map?${ZONE_PARAM}=${encodeURIComponent(zone.id)}`, {
            state: { focusZone: true },
          });
        }}
        className="mt-6"
      />
    </div>
  );
}
